import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import { Box, Typography } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import Fuse from 'fuse.js';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useDebounce } from 'use-debounce';
import { formatEmail } from 'utils/common/formatEmail';

import Button from 'components/Button';
import CustomSearchbar from 'components/CustomSearchBar';
import DeviceList from 'components/DeviceComponents/DeviceList';
import RightComponentWrapper from 'components/DeviceComponents/RightComponentWrapper';
import ErrorText from 'components/ErrorText';

import { useAppDispatch, useAppSelector } from 'redux-store';
import assignDevicesToMemberThunk from 'redux-store/thunks/organizationOverview/assignDevicesToMemberThunk';
import inviteMemberToOrganizationThunk from 'redux-store/thunks/organizationOverview/inviteMemberToOrganizationThunk';
import getOrganizationByIdThunk from 'redux-store/thunks/organizations/getOrganizationByIdThunk';

import type { AddMemberToOrganizationModalStageProps } from '..';

const AddDeviceToUserAccount: FC<AddMemberToOrganizationModalStageProps> = ({
  state,
  setState,
  onCloseProxy,
  onClose,
  organizationId,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [searchString, setSearchString] = useState('');
  const [searchResults, setSearchResults] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fullStatus = useAppSelector(appState => appState.api.fullStatus);
  const organizationName = useAppSelector(s => {
    const org = s.organizations.items.find(o => o.id === organizationId);

    return org?.name ?? null;
  });

  const organizationDevices = useAppSelector(
    s => s.organizations.selectedOrganization.organization?.devices ?? [],
  );

  const memberCount = (id: number): number | undefined =>
    organizationDevices.find(d => d.id === id)?.users.length;

  const devicesWithNames = useMemo(() => {
    return organizationDevices.map(device => ({
      ...device,
      name: fullStatus[device.serial]?.fna,
    }));
  }, [organizationDevices, fullStatus]);

  const fuse = useMemo(
    () =>
      new Fuse(devicesWithNames, {
        keys: ['name', 'serial'],
        threshold: 0.4,
      }),
    [devicesWithNames],
  );

  const handleSearch = useCallback((): void => {
    if (!searchString) {
      setSearchResults(null);

      return;
    }

    const results = fuse.search(searchString);

    setSearchResults(results.map(result => result.item.serial));
  }, [fuse, searchString]);

  const [debouncedSearch] = useDebounce(searchString, 500);

  useEffect(() => {
    if (debouncedSearch) {
      handleSearch();
    }
  }, [debouncedSearch, handleSearch]);

  const handleClearSearch = useCallback((): void => {
    setSearchString('');
    setSearchResults(null);
  }, []);

  const selectedDeviceIds = useMemo(() => state.devices, [state.devices]);
  const setSelectedDeviceIds = useCallback(
    (devices: number[]) => {
      setState(prev => ({ ...prev, devices }));
    },
    [setState],
  );

  const filteredDevices = useMemo(
    () =>
      searchResults
        ? devicesWithNames.filter(device =>
            searchResults.includes(device.serial),
          )
        : devicesWithNames,
    [devicesWithNames, searchResults],
  );

  const handleAfterSubmit = useCallback(
    async (id: number) => {
      setState(prev => ({
        ...prev,
        stage: 'success',
      }));

      await dispatch(
        getOrganizationByIdThunk({
          organizationId: id,
        }),
      ).unwrap();

      setTimeout(() => {
        setState(prev => ({
          ...prev,
          stage: 'email',
        }));
        onClose();
      }, 1250);
    },
    [dispatch, onClose, setState],
  );

  const handleAssignDevicesToMember = useCallback(
    async (orgId: number, newUserID: number) => {
      let result = undefined;

      try {
        setLoading(true);

        result = await dispatch(
          assignDevicesToMemberThunk({
            organizationId: orgId,
            userId: newUserID,
            data: {
              devices: selectedDeviceIds,
            },
          }),
        ).unwrap();

        if (!result.success) {
          setError(
            t('common:pages.organization_overview.add_user.cannot_assign'),
          );
          setLoading(false);

          return;
        }

        setError(null);
        handleAfterSubmit(orgId);
      } catch (e) {
        Sentry.captureException(e, {
          extra: {
            state,
            result,
          },
        });
      } finally {
        setLoading(false);
      }
    },
    [dispatch, handleAfterSubmit, selectedDeviceIds, state, t],
  );

  const handleInviteMemeber = useCallback(async () => {
    if (!organizationId) return;

    const formattedEmail = formatEmail(state.email);

    try {
      setLoading(true);

      const invitedMember = await dispatch(
        inviteMemberToOrganizationThunk({
          organizationId: organizationId,
          data: {
            email: formattedEmail,
            role: state.role,
          },
        }),
      ).unwrap();

      if (!invitedMember.success) {
        setError(invitedMember.error);
        setLoading(false);

        return;
      }

      if (state.role === 'member' && selectedDeviceIds.length !== 0) {
        handleAssignDevicesToMember(organizationId, invitedMember.user_id);
      } else {
        handleAfterSubmit(organizationId);
      }

      setError(null);
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          state,
        },
      });
    } finally {
      setLoading(false);
    }
  }, [
    dispatch,
    handleAfterSubmit,
    handleAssignDevicesToMember,
    organizationId,
    selectedDeviceIds.length,
    state,
  ]);

  return (
    <>
      <Box
        sx={{
          height: '45vh',
          overflow: 'auto',
          paddingBottom: 5,
          width: '100%',
        }}
      >
        <Box mb={4}>
          <Typography
            variant="h2"
            mb={2}
            textAlign="center"
            data-testid="select-devices-text"
          >
            {t('common:pages.organization_overview.add_user.select_devices')}
          </Typography>
          <Typography variant="body1" mb={2} textAlign="center">
            {t(
              state.role === 'member'
                ? 'common:pages.organization_overview.add_user.devices_member_info'
                : 'common:pages.organization_overview.add_user.devices_org_admin_info',
            )}
          </Typography>
        </Box>
        {organizationDevices.length > 0 ? (
          <>
            <Typography
              variant="h3"
              mb={1}
              data-testid="organisation-devices-text"
            >
              {t('common:pages.organization_overview.add_user.org_devices', {
                organization: organizationName,
              })}
            </Typography>
            <Box mb={3} width="100%">
              <CustomSearchbar
                search={searchString}
                setSearch={setSearchString}
                hasSearched={searchString.length > 0}
                handleSearch={handleSearch}
                handleClearSearch={handleClearSearch}
                testId="organisation-devices-search-bar"
              />
            </Box>
            {filteredDevices.length > 0 ? (
              filteredDevices.map(item => (
                <DeviceList
                  key={item.id}
                  deviceId={item.id}
                  serial={item.serial}
                  selectable={state.role === 'member'}
                  selectedDeviceIds={selectedDeviceIds}
                  setSelectedDeviceIds={setSelectedDeviceIds}
                  rightComponent={
                    memberCount(item.id) ? (
                      <RightComponentWrapper>
                        <GroupOutlinedIcon
                          fontSize="small"
                          sx={{ color: 'grey.500' }}
                        />
                        <Typography
                          data-testid="number-of-members"
                          variant="body1"
                          noWrap
                        >
                          {memberCount(item.id)}
                        </Typography>
                      </RightComponentWrapper>
                    ) : null
                  }
                />
              ))
            ) : (
              <Typography
                variant="body1"
                color="grey.600"
                textAlign="center"
                data-testid="search-empty-text"
              >
                {t('common:pages.organization_overview.add_user.search_empty')}
              </Typography>
            )}
          </>
        ) : (
          <Typography
            variant="body1"
            color="grey.600"
            textAlign="center"
            data-testid="no-devices-found-result-text"
          >
            {t('common:pages.organization_overview.add_user.no_devices_found')}
          </Typography>
        )}
        <ErrorText error={error} />
      </Box>
      <Box
        width="100%"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        mt={2}
        gap={2}
      >
        <Button
          variant="flat"
          color="neutral"
          size="large"
          onClick={onCloseProxy}
          startIcon={<KeyboardArrowLeftRoundedIcon />}
          data-testid="modal-previous-button"
        >
          {t('common:previous')}
        </Button>
        <Button
          variant="flat"
          size="large"
          onClick={handleInviteMemeber}
          disabled={loading}
          data-testid="modal-next-button"
        >
          {t('common:finish')}
        </Button>
      </Box>
    </>
  );
};

export default AddDeviceToUserAccount;
